import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiJson } from '@/api/client'
import { onTokensChanged } from '@/lib/auth-tokens'
import { logout as logoutCall } from './api'
import type { Me } from './types'

export const meQueryKey = ['me'] as const

/**
 * Current user. Cached app-wide; staleTime is high because permissions
 * rarely change within a session and the backend invalidates via
 * WebSocket on rotation.
 *
 * Always fires (even without tokens) so feature tests that don't set up
 * a session keep working — the API client + MSW handler decide whether
 * the call succeeds. A 401 won't retry; the route guard bounces to
 * /login.
 */
export function useMe() {
  const qc = useQueryClient()

  // Refresh the cached `me` whenever tokens change (login / logout /
  // token-refresh) so permission-gated UI re-evaluates without polling.
  useEffect(() => {
    return onTokensChanged(() => {
      void qc.invalidateQueries({ queryKey: meQueryKey })
    })
  }, [qc])

  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => apiJson<Me>('/me'),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => {
      // 401 means logged out; don't keep retrying.
      if (err && typeof err === 'object' && 'status' in err && err.status === 401) return false
      return failureCount < 2
    },
  })
}

/**
 * Logout flow: revokes server-side tokens, clears the local store,
 * resets the React Query cache so no stale data leaks into the next
 * session, and redirects to /login.
 *
 * When the platform's OIDC backend is enabled (probed via
 * /api/v1/auth/oidc/status), this also performs RP-Initiated Logout:
 * after the platform-side logout completes, the browser navigates
 * to /api/v1/auth/oidc/end-session which 302s to the IdP's
 * end_session_endpoint. The IdP terminates its own session + sends
 * the browser back to /login. Without this round-trip the IdP
 * keeps the SSO session alive and clicking 'Sign in with SSO'
 * a second time silently re-issues an auth code without prompting.
 */
export function useLogout() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async () => {
      // Best-effort platform-side logout (revoke + clear cookies +
      // localStorage). Failing this still proceeds — the IdP-side
      // logout is more important for SSO posture.
      try {
        await logoutCall()
      } catch {
        /* surfaced via logoutCall's own log; not user-visible */
      }
      // Probe whether OIDC is enabled. If yes, hand off to the
      // end-session endpoint (full-page navigation so the browser
      // follows the 302 chain to the IdP + back). If no, return
      // false so the onSuccess path does the SPA /login navigate.
      try {
        const r = await fetch('/api/v1/auth/oidc/status')
        const status = r.ok
          ? ((await r.json()) as { enabled?: boolean })
          : { enabled: false }
        return status.enabled === true
      } catch {
        return false
      }
    },
    onSuccess: (oidcEnabled) => {
      qc.clear()
      if (oidcEnabled) {
        // Full-page navigation so the browser follows IdP redirects.
        window.location.href = '/api/v1/auth/oidc/end-session'
        return
      }
      void navigate('/login', { replace: true })
    },
  })
}
